import {
  Component,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import { finalize, take } from 'rxjs';
import { Perfil, PerfilFiltros, PerfilStatus } from '../../core/models/perfil.model';
import { PerfilService } from '../../core/services/perfil.service';
import { ToastService } from '../../core/services/toast.service';
import { ApiHttpError } from '../../shared/services/http-client/api-http-error.model';
import { LoaderService } from '../../shared/services/loader/loader.service';
import { formatDateIsoToBr } from '../../core/utils/date-format.util';
import { EmptyStateComponent } from '../../shared/components/empty-state/empty-state.component';
import { PaginationComponent } from '../../shared/components/pagination/pagination.component';
import { AlertDialogComponent } from '../../shared/components/alert-dialog/alert-dialog.component';
import { StatusBadgeComponent } from '../../shared/components/status-badge/status-badge.component';
import { DataTableComponent } from '../../shared/components/data-table/data-table.component';
import { BackButtonComponent } from '../../shared/components/back-button/back-button.component';
import { PageHeaderComponent } from '../../shared/components/page-header/page-header.component';
import {
  FilterBarComponent,
  FilterField,
} from '../../shared/components/filter-bar/filter-bar.component';
import { PerfilVisualizarModalComponent } from './perfil-visualizar-modal.component';
import {
  ActionsMenuPosition,
  adjustActionsMenuPosition,
  getActionsMenuPosition,
} from '../../shared/utils/actions-menu.util';

const PAGE_SIZE = 10;
const STATUS_TODOS = 'todos';
/** A listagem entra filtrada por Ativo; "Limpar" devolve o campo para Todos. */
const STATUS_PADRAO_LISTAGEM: PerfilStatus = 'Ativo';

@Component({
  selector: 'app-perfil-list',
  standalone: true,
  imports: [
    BackButtonComponent,
    PageHeaderComponent,
    FilterBarComponent,
    EmptyStateComponent,
    PaginationComponent,
    AlertDialogComponent,
    StatusBadgeComponent,
    DataTableComponent,
    PerfilVisualizarModalComponent,
  ],
  templateUrl: './perfil-list.component.html',
  styleUrl: './perfil-list.component.scss',
})
export class PerfilListComponent {
  private readonly perfilService = inject(PerfilService);
  private readonly router = inject(Router);
  private readonly toastService = inject(ToastService);
  private readonly loaderService = inject(LoaderService);
  private readonly injector = inject(Injector);

  private readonly perfisSignal = signal<readonly Perfil[]>([]);

  readonly perfis = this.perfisSignal.asReadonly();
  readonly currentPage = signal(1);
  readonly menuAbertoId = signal<number | null>(null);
  readonly menuPosition = signal<ActionsMenuPosition | null>(null);
  readonly pageSize = PAGE_SIZE;
  readonly formatDate = formatDateIsoToBr;

  // Controle do diálogo de confirmação de exclusão.
  showDeleteDialog = false;
  perfilSelecionadoParaExcluir?: Perfil;

  // Controle do modal de visualização do perfil.
  private readonly perfilVisualizadoSignal = signal<Perfil | null>(null);

  /** Perfil exibido no modal de visualização; `null` quando o modal está fechado. */
  readonly perfilVisualizado = this.perfilVisualizadoSignal.asReadonly();
  readonly showVisualizarModal = computed(() => this.perfilVisualizado() !== null);

  /**
   * Last filter record emitted by `app-filter-bar`.
   * Used by `filtrosAtuais()` to rebuild `PerfilFiltros` after a deletion
   * without depending on a local form.
   */
  private readonly ultimosFiltros = signal<Record<string, string>>({});

  /** Filter-bar field descriptors for the perfil list. */
  readonly filterFields: FilterField[] = [
    {
      key: 'nome',
      label: 'Procure por nome',
      type: 'text',
      placeholder: 'Digite o nome do perfil',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      defaultValue: STATUS_PADRAO_LISTAGEM,
      clearValue: STATUS_TODOS,
      options: [
        { value: STATUS_TODOS, label: 'Todos' },
        { value: 'Ativo', label: 'Ativo' },
        { value: 'Inativo', label: 'Inativo' },
      ],
    },
    {
      key: 'data',
      label: 'Data',
      type: 'date',
    },
  ];

  readonly perfisPaginados = computed(() => {
    const inicio = (this.currentPage() - 1) * PAGE_SIZE;
    return this.perfisSignal().slice(inicio, inicio + PAGE_SIZE);
  });

  /**
   * Called by `app-filter-bar` on submit and on init.
   * Replaces the previous `buscar()` + `limparFiltros()` pair.
   */
  onFiltersChange(record: Record<string, string>): void {
    this.ultimosFiltros.set(record);
    this.currentPage.set(1);
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
    this.carregarPerfis(this.recordToFiltros(record));
  }

  voltar(): void {
    void this.router.navigate(['/']);
  }

  criarPerfil(): void {
    void this.router.navigate(['/perfis/novo']);
  }

  onConfiguracoes(): void {
    void this.router.navigate(['/perfil/configuracoes']);
  }

  /** Abre o modal de visualização com o perfil da linha, sem sair da listagem. */
  visualizarPerfil(id: number): void {
    this.menuAbertoId.set(null);

    const perfil = this.perfis().find((item) => item.id === id);
    if (!perfil) {
      return;
    }

    this.perfilVisualizadoSignal.set(perfil);
    this.carregarCategoriasDoPerfilVisualizado(perfil);
  }

  fecharVisualizacao(): void {
    this.perfilVisualizadoSignal.set(null);
  }

  /** Fecha o modal antes de navegar para a edição, evitando estado residual. */
  editarPerfilVisualizado(id: number): void {
    this.fecharVisualizacao();
    this.editarPerfil(id);
  }

  editarPerfil(id: number): void {
    this.menuAbertoId.set(null);
    void this.router.navigate(['/perfil', id, 'editar']);
  }

  abrirConfirmacaoExclusao(perfil: Perfil): void {
    this.menuAbertoId.set(null);
    this.perfilSelecionadoParaExcluir = perfil;
    this.showDeleteDialog = true;
  }

  cancelarExclusao(): void {
    this.showDeleteDialog = false;
    this.perfilSelecionadoParaExcluir = undefined;
  }

  confirmarExclusao(): void {
    const perfil = this.perfilSelecionadoParaExcluir;
    if (!perfil) {
      this.showDeleteDialog = false;
      return;
    }

    this.loaderService.showLoader();
    this.perfilService
      .deletePerfil(perfil.id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: () => {
          this.carregarPerfis(this.filtrosAtuais());
          this.toastService.show('Perfil excluído com sucesso.', 'success');
          this.showDeleteDialog = false;
          this.perfilSelecionadoParaExcluir = undefined;
        },
        error: (error: unknown) => {
          this.showDeleteDialog = false;
          this.perfilSelecionadoParaExcluir = undefined;
          const message =
            error instanceof ApiHttpError ? error.message : 'Não foi possível excluir o perfil.';
          this.toastService.show(message, 'error');
        },
      });
  }

  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
  }

  toggleMenu(id: number, event: Event): void {
    event.stopPropagation();

    if (this.menuAbertoId() === id) {
      this.menuAbertoId.set(null);
      this.menuPosition.set(null);
      return;
    }

    this.menuAbertoId.set(id);
    this.menuPosition.set(getActionsMenuPosition(event));
    this.adjustMenuPosition();
  }

  private adjustMenuPosition(): void {
    afterNextRender(
      () => {
        const adjusted = adjustActionsMenuPosition(this.menuPosition());
        if (adjusted) {
          this.menuPosition.set(adjusted);
        }
      },
      { injector: this.injector },
    );
  }

  @HostListener('document:click')
  closeMenu(): void {
    this.menuAbertoId.set(null);
    this.menuPosition.set(null);
  }

  // ── private helpers ──────────────────────────────────────────────────────────

  /**
   * A listagem (`GET /v1/PerfilInvestimento`) não devolve as categorias do
   * perfil; o modal as carrega sob demanda e mescla no perfil já exibido.
   */
  private carregarCategoriasDoPerfilVisualizado(perfil: Perfil): void {
    this.loaderService.showLoader();
    this.perfilService
      .getCategoriasByPerfil(perfil.id)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (categorias) => {
          // Ignora a resposta se o modal já foi fechado ou trocou de perfil.
          this.perfilVisualizadoSignal.update((atual) =>
            atual?.id === perfil.id ? { ...atual, categorias } : atual,
          );
        },
        error: () =>
          this.toastService.show(
            'Não foi possível carregar as categorias do perfil.',
            'error',
          ),
      });
  }

  private carregarPerfis(filtros?: PerfilFiltros): void {
    this.loaderService.showLoader();
    this.perfilService
      .getPerfis(filtros)
      .pipe(
        take(1),
        finalize(() => this.loaderService.hideLoader()),
      )
      .subscribe({
        next: (perfis) => {
          this.perfisSignal.set(perfis);
          this.ajustarPaginaAtual();
        },
        error: () => {
          this.perfisSignal.set([]);
          this.ajustarPaginaAtual();
          this.toastService.show('Não foi possível carregar os perfis.', 'error');
        },
      });
  }

  /** Garante que a página atual continue válida após filtros/exclusões. */
  private ajustarPaginaAtual(): void {
    const totalPaginas = Math.max(1, Math.ceil(this.perfis().length / PAGE_SIZE));
    if (this.currentPage() > totalPaginas) {
      this.currentPage.set(totalPaginas);
    }
  }

  /**
   * Rebuilds `PerfilFiltros` from the last emitted filter record.
   * Used after deletion to reload the list with the same criteria.
   */
  private filtrosAtuais(): PerfilFiltros {
    return this.recordToFiltros(this.ultimosFiltros());
  }

  private recordToFiltros(record: Record<string, string>): PerfilFiltros {
    const filtros: PerfilFiltros = {};

    const nome = (record['nome'] ?? '').trim();
    if (nome) {
      filtros.nome = nome;
    }

    const status = record['status'];
    if (status && status !== STATUS_TODOS) {
      filtros.status = status;
    }

    if (record['data']) {
      filtros.data = record['data'];
    }

    return filtros;
  }
}
